import * as rd from '@devexperts/remote-data-ts'
import { RemoteData } from '@devexperts/remote-data-ts'

import { createSelector } from '@reduxjs/toolkit'
import { resetAllState } from '@/store/commonActions.ts'
import * as remoteDataUtils from '@/utils/remoteData.ts'
import { httpClient } from '../lib/http/HttpClient.ts'
import { Country } from '../models/app.ts'
import { ResponseBase } from '../models/http.ts'
import { getHalfDayValueFromBookingPeriod } from '../utils/halfDay.ts'

import { appSlice } from './appSlice.ts'
import { BookingAvailability } from './bookingAvailabilitySlice.ts'
import { cartSlice } from './cartSlice/index.ts'
import { availableFeaturedServicesSelector } from './cartSlice/selectors/selectAvailableFeaturedServices.ts'
import { RootState } from './configureStore.ts'
import { createAppSlice } from './createAppSlice.ts'
import { PeriodSliceState } from './insertPeriodSlice.ts'
import { licenseSlice } from './licenseSlice/index.tsx'
import { mapSelectors } from './mapSlice.ts'
import { servicesSlice } from './servicesSlice'

interface ExtraState {
  weatherInsurancePricePreview: RemoteData<unknown, number>
  isWeatherInsuranceInitiallyVisible?: boolean
}

const initialState: ExtraState = {
  weatherInsurancePricePreview: rd.initial,
}

const DEFAULT_PRICE_PREVIEW_AMOUNT = 50

export const extraSlice = createAppSlice({
  extraReducers(builder) {
    builder.addCase(resetAllState, () => initialState)
  },
  initialState,
  name: 'extra',
  reducers: (create) => ({
    fetchWeatherInsurancePricePreview: create.asyncThunk<
      number,
      { amount?: number } | undefined
    >(
      async ({ amount = DEFAULT_PRICE_PREVIEW_AMOUNT } = {}, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState
        const country = appSlice.selectors.country(rootState)
        const license = licenseSlice.selectors.licenseCode(rootState)

        if (amount === 0) {
          return 0
        }

        const res = await fetchPricePreview({
          amount,
          country,
          insertPeriod: rootState.insertPeriod,
          license,
        })

        if (res.status === 'error') {
          throw thunkAPI.rejectWithValue(res.error)
        }

        if (res.data.error !== null) {
          throw thunkAPI.rejectWithValue(res.data.error)
        }

        return res.data.result.price
      },
      {
        fulfilled: (state, action) => {
          state.weatherInsurancePricePreview = rd.success(action.payload)
          state.isWeatherInsuranceInitiallyVisible = action.payload > 0
        },
        pending: (state) => {
          state.weatherInsurancePricePreview = rd.pending
        },
        rejected: (state, action) => {
          state.weatherInsurancePricePreview = rd.failure(action.payload)
        },
      }
    ),
  }),
  selectors: {
    isWeatherInsuranceInitiallyVisible: (state) =>
      state.isWeatherInsuranceInitiallyVisible,
    weatherInsurancePricePreview: (state) => state.weatherInsurancePricePreview,
  },
})

interface FetchPricePreviewProps {
  insertPeriod: PeriodSliceState
  license: string
  amount?: number
  country: Country
}

type FetchPricePreviewResponse = ResponseBase<{ price: number }>

export const fetchPricePreview = (props: FetchPricePreviewProps) => {
  const { license, country, insertPeriod } = props
  const { period, bookingPeriod } = insertPeriod

  return httpClient.fetch<FetchPricePreviewResponse>(
    `beaches/${license}/weather-insurance/price-preview`,
    country,
    {
      body: JSON.stringify({
        amount: props.amount,
        from: new Date(period?.start || Date.now()).getTime() / 1000,
        halfDay: getHalfDayValueFromBookingPeriod(bookingPeriod),
        to: new Date(period?.end || Date.now()).getTime() / 1000,
      }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    }
  )
}

export const selectIsExtraStepVisible = createSelector(
  [
    extraSlice.selectors.weatherInsurancePricePreview,
    availableFeaturedServicesSelector,
  ],
  (priceRemoteData, featuredServices) => {
    return (
      remoteDataUtils.onSuccess(priceRemoteData, (price) => price > 0) ||
      (featuredServices && featuredServices.length > 0)
    )
  }
)

export const selectIsProgressBarPending = createSelector(
  [
    servicesSlice.selectors.services,
    mapSelectors.mapRemoteData,
    cartSlice.selectors.selectedBookingType,
  ],
  (services, mapData, selectedBookingType) => {
    if (selectedBookingType === BookingAvailability.TICKETS) {
      return rd.isPending(services)
    }

    return rd.isPending(services) || rd.isPending(mapData)
  }
)
